import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueries, useQuery as useTanStackQuery } from '@tanstack/react-query';
import { Circle, Marker, Polygon, Polyline } from '@react-google-maps/api';
import { Alert, Box, Button, Checkbox, FilterSelect, Map, Stack, Typography } from '../../ui/core';
import { cardHeaderSx, filterPillSx, pillButtonSx } from '../../ui/styles';
import { StationIcon } from '../../ui/icons';
import { useCities, useDebouncedValue, useQuery } from '../../hooks';
import laneMapApi from '../../lib/api/LaneMapApi';
import { useHasPermission } from '../../helpers/api/auth';
import { PERMISSIONS } from '../../helpers/api/auth/permissions';
import LaneMapLimitsPanel from './LaneMapLimitsPanel';
import LaneMapLegend from './LaneMapLegend';
import { CIRCLE_SYMBOL_PATH } from '../../ui/core/mapSymbolPaths';
import {
  AVAILABILITY_KIND,
  STATION_PAIR_VERDICTS,
  countUsableStationsForPair,
  deserializeLimits,
  flattenLiveLimits,
  getStationAvailabilityForPair,
  getStationVerdictForPair,
  serializeLimits,
} from './laneEligibility';
import {
  buildCityPairs,
  geoJsonToPath,
  geoJsonToPolygonPaths,
  laneColorFor,
} from './laneGeometry';

/**
 * The pure geometry/grouping helpers now live in `./laneGeometry` so other
 * pages can draw lane polylines without importing this component. They are
 * re-exported here because this file was their original home: `LaneMap.test.js`
 * imports them from `./LaneMap`, and keeping the re-exports means that suite
 * stays an untouched regression net across the move.
 */
export {
  LANE_COLORS,
  laneColorFor,
  geoJsonToPath,
  geoJsonToPolygonPaths,
  cityPairKey,
  buildCityPairs,
} from './laneGeometry';

const DEFAULT_CENTER = { lat: 30.2672, lng: -97.7431 };
const DEFAULT_ZOOM = 8;

/**
 * Lat/lng for each of a pair's two cities, read off whichever route has them
 * (every route in the pair should agree, since they're the same two cities;
 * scanning all of them just means a single route with a missing coordinate
 * doesn't blank out the whole pair). Returns `null` for a city whose
 * coordinates never show up on any route.
 */
const cityCoordsForPair = (pair) => {
  let coordsA = null;
  let coordsB = null;
  pair.routes.forEach((route) => {
    if (
      route.originCityName === pair.cityA &&
      Number.isFinite(route.originCityLat) &&
      Number.isFinite(route.originCityLng)
    ) {
      coordsA = { lat: route.originCityLat, lng: route.originCityLng };
    }
    if (
      route.destinationCityName === pair.cityA &&
      Number.isFinite(route.destinationCityLat) &&
      Number.isFinite(route.destinationCityLng)
    ) {
      coordsA = { lat: route.destinationCityLat, lng: route.destinationCityLng };
    }
    if (
      route.originCityName === pair.cityB &&
      Number.isFinite(route.originCityLat) &&
      Number.isFinite(route.originCityLng)
    ) {
      coordsB = { lat: route.originCityLat, lng: route.originCityLng };
    }
    if (
      route.destinationCityName === pair.cityB &&
      Number.isFinite(route.destinationCityLat) &&
      Number.isFinite(route.destinationCityLng)
    ) {
      coordsB = { lat: route.destinationCityLat, lng: route.destinationCityLng };
    }
  });
  return { coordsA, coordsB };
};

const CANDIDATE_COLOR = '#1B5E20';
const EXCLUDED_COLOR = '#EF6C00';
const NEUTRAL_COLOR = '#9E9E9E';

/**
 * Marker style for one of the three `STATION_PAIR_VERDICTS`, or neutral when
 * `verdict` is null/undefined — which happens both when no pair is selected
 * and for a viewport-only station that never appeared in any per-route
 * station fetch. Corridor-relative state is meaningless in that case, so we
 * render a neutral dot rather than misreport the station as rejected.
 */
const stationIcon = (station, verdict) => {
  // A path literal, not `SymbolPath.CIRCLE`: this runs in render, before Maps
  // has loaded. See ui/core/mapSymbolPaths.
  const circle = CIRCLE_SYMBOL_PATH;

  // Non-operational wins over every verdict. Before this, a switched-off
  // station rendered as IN_CORRIDOR_UNUSABLE — indistinguishable from one the
  // limits excluded, which is the opposite conclusion for an operator: one
  // means "somebody turned this off", the other means "your limits reject it".
  // On staging's Austin↔Houston pair, ALL 14 in-corridor-unusable stations were
  // merely non-operational, so the orange bucket was reporting almost nothing
  // about eligibility. Faded and hollow-with-a-dot, distinct from both the
  // orange ring and the small solid OUTSIDE_CORRIDOR dot.
  if (!station.isOperational) {
    return {
      path: circle,
      fillColor: NEUTRAL_COLOR,
      fillOpacity: 0.35,
      strokeColor: NEUTRAL_COLOR,
      strokeWeight: 1.5,
      strokeOpacity: 0.8,
      scale: 5,
    };
  }

  if (!verdict) {
    return {
      path: circle,
      fillColor: '#37474F',
      fillOpacity: 1,
      strokeColor: '#FFFFFF',
      strokeWeight: 1,
      scale: 4,
    };
  }

  if (verdict === STATION_PAIR_VERDICTS.ELIGIBLE) {
    return {
      path: circle,
      fillColor: CANDIDATE_COLOR,
      fillOpacity: 1,
      strokeColor: '#FFFFFF',
      strokeWeight: 1.5,
      scale: 7,
    };
  }

  if (verdict === STATION_PAIR_VERDICTS.IN_CORRIDOR_UNUSABLE) {
    // Hollow so it reads as "considered and dropped".
    return {
      path: circle,
      fillColor: '#FFFFFF',
      fillOpacity: 1,
      strokeColor: EXCLUDED_COLOR,
      strokeWeight: 2.5,
      scale: 6,
    };
  }

  // OUTSIDE_CORRIDOR: small, grey, recessed.
  return {
    path: circle,
    fillColor: NEUTRAL_COLOR,
    fillOpacity: 0.7,
    strokeColor: '#FFFFFF',
    strokeWeight: 0.5,
    scale: 3.5,
  };
};

/**
 * The city name on an availability entry is the route's ORIGIN city for a
 * pickup and its DESTINATION city for a dropoff — it is *not* the city the
 * station sits in, and it usually isn't. The far-city rule in
 * `evaluateStationForRoute` qualifies a station for pickup at the origin only
 * when it is at least `farCityExclusionMeters` from the DESTINATION, so a
 * qualifying station is by construction far from the city named on its dropoff
 * line. Bastrop Starbucks, for instance, is a legitimate dropoff for
 * Austin→Houston while sitting ~230km from Houston.
 *
 * So the phrasing is directional ("for rides to Houston") rather than
 * locational ("in Houston"), which would assert something false about most
 * qualifying stations.
 */
const AVAILABILITY_PHRASE = {
  [AVAILABILITY_KIND.PICKUP]: (cityName) => `Available for Pickup on rides from ${cityName}`,
  [AVAILABILITY_KIND.DROPOFF]: (cityName) => `Available for Dropoff on rides to ${cityName}`,
};

// Pickups before dropoffs, so a pair covering both directions reads as two
// grouped statements rather than four interleaved ones.
const AVAILABILITY_ORDER = [AVAILABILITY_KIND.PICKUP, AVAILABILITY_KIND.DROPOFF];

/**
 * Builds the marker tooltip. `getStationAvailabilityForPair` deliberately
 * returns data, not strings (see laneEligibility.js), so wording and
 * ordering live here. Google Maps marker `title` renders `\n` as line breaks.
 *
 * The header is the station NAME ALONE — deliberately no parenthetical.
 *
 * It used to read `${name} (${routeType})`, which rendered "(undefined)" on
 * every marker in production once hitch-services#3469 dropped
 * `station.route_type` (and the `route_type` enum) and removed it from both
 * `stationsInBounds` and `stationsForLane`. That migration's own note lists the
 * readers it accounted for and stops at the backend, so this UI — the only
 * surface that actually *rendered* the value — was missed.
 *
 * Not replaced with anything, rather than repointed at `partnerType`:
 * - The parenthetical never carried information. All 975 postgres rows were
 *   PICK_UP_AND_DROP_OFF (the backfill normalised mongo's real 566/527 split
 *   away, and admin's create path hardcoded it), so it was one constant
 *   repeated on every marker. Nothing was lost when it broke.
 * - `partnerType` IS on the wire, but plays no part in this page: neither
 *   `stationIcon`, the legend, nor `laneEligibility` reads it. This map answers
 *   "is this station usable for this pair?", and a REGULAR/PARTNER/HUB tag
 *   invites a question the rest of the page can't answer. `PartnerTypeChip` is
 *   where that belongs, and the stations table already renders it there.
 * - The station builder's sibling marker tooltip (`existingStationTooltip`)
 *   likewise identifies a station by plain facts and carries no parenthetical.
 *
 * The lines below the header are the ones doing the work.
 */
const stationTooltip = (station, verdict, availability) => {
  const header = station.name;

  // Mirrors stationIcon's precedence: say WHY it's unusable rather than
  // letting it read as a limits exclusion.
  if (!station.isOperational) return `${header}\nNot operational`;

  if (!verdict) return header;

  if (verdict === STATION_PAIR_VERDICTS.ELIGIBLE) {
    const lines = [...availability]
      .sort(
        (a, b) => AVAILABILITY_ORDER.indexOf(a.kind) - AVAILABILITY_ORDER.indexOf(b.kind),
      )
      .map(({ kind, cityName }) => AVAILABILITY_PHRASE[kind](cityName));
    return [header, ...lines].join('\n');
  }

  if (verdict === STATION_PAIR_VERDICTS.IN_CORRIDOR_UNUSABLE) {
    return `${header}\nIn a route corridor, but not usable for this pair`;
  }

  return `${header}\nOutside every route corridor in this pair`;
};

/**
 * Fans a buffer fetch out across every route passed in — one fetchLaneBuffer
 * call per route, all sharing the same radius — via `useQueries`'s
 * `{ queries: [...] }` signature (v4). For a pair with N operational routes
 * and this layer enabled, that's N requests; for Austin↔Houston (6
 * operational routes) that's 6. Each is still cached by (laneId,
 * radiusMeters) exactly as a single-lane fetch was, so toggling a layer
 * on/off or dragging the radius back to a previously-seen value is served
 * from cache (`staleTime: Infinity`).
 */
const useRouteBuffers = (routes, radiusMeters, enabled) =>
  useQueries({
    queries: routes.map((route) => ({
      queryKey: ['lane-buffer', route.id, radiusMeters],
      queryFn: () => laneMapApi.fetchLaneBuffer({ laneId: route.id, radiusMeters }),
      enabled: enabled && !!radiusMeters,
      staleTime: Infinity,
    })),
  });

/**
 * The single place that builds the limits-panel URL. Always routes the limit
 * params through `serializeLimits` (the same function the panel's own
 * onChange path uses) rather than re-deriving which keys are overrides here,
 * so a city switch and a slider edit can never disagree about what belongs
 * in the query string. `limits` is `null` on the very first renders before
 * the live values have loaded; buildURLParams skips serialising in that case.
 */
const buildURLParams = (cityId, limits, liveLimits) => {
  const params = new URLSearchParams();
  if (cityId) params.append('cityId', cityId);
  if (limits) {
    new URLSearchParams(serializeLimits(limits, liveLimits)).forEach((value, key) =>
      params.append(key, value),
    );
  }
  return params;
};

const LaneMap = () => {
  const history = useHistory();
  const canEditPolyline = useHasPermission(PERMISSIONS.EDIT_LANE_POLYLINE);

  const { cityId } = useQuery();

  const [map, setMap] = useState(null);
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [hiddenPairKeys, setHiddenPairKeys] = useState([]);
  const [selectedPairKey, setSelectedPairKey] = useState(null);
  const [visibleLayers, setVisibleLayers] = useState({
    stationCorridor: true,
    sharedDoorBand: true,
    // Off by default: at live values this band is exactly coincident with the
    // station corridor (both 10,000 m), so showing both by default reads as one
    // band rather than two. Turn it on to see the along-route bound.
    alongRouteBound: false,
    farCityExclusion: true,
    laneMatchOuter: false,
    cityRadius: false,
    // Off by default. Non-operational stations are noise for the question this
    // page answers ("where do the limits bite?") and they dominate the
    // not-eligible markers — 14 of 14 on staging's Austin↔Houston pair. Hiding
    // them means every remaining non-green marker is a genuine limits outcome.
    nonOperationalStations: false,
  });
  const initialLoadDone = useRef(false);

  const toggleLayer = useCallback((key) => {
    setVisibleLayers((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const { cities: citiesOptions } = useCities({ operationalOnly: true, sorted: true });

  const selectedCity = useMemo(
    () => (cityId ? citiesOptions.find((c) => c.id === cityId) || null : null),
    [cityId, citiesOptions],
  );

  const getBoundsFromMap = useCallback((mapInstance) => {
    const b = mapInstance?.getBounds();
    if (!b) return null;
    const sw = b.getSouthWest();
    const ne = b.getNorthEast();
    return { swLng: sw.lng(), swLat: sw.lat(), neLng: ne.lng(), neLat: ne.lat() };
  }, []);

  const onMapLoad = useCallback((mapInstance) => setMap(mapInstance), []);

  const onMapIdle = useCallback(() => {
    if (!map) return;
    setZoom(map.getZoom());
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      const b = getBoundsFromMap(map);
      if (b) setBounds(b);
    }
  }, [map, getBoundsFromMap]);

  const onSearchArea = useCallback(() => {
    if (!map) return;
    const b = getBoundsFromMap(map);
    if (b) setBounds(b);
  }, [map, getBoundsFromMap]);

  // isLoading is `status === 'loading'`, not "fetch in flight" — for a
  // disabled query (enabled: false) that has never fetched, status stays
  // 'loading' forever, so it renders "Loading..." permanently before a city
  // is picked / before bounds exist. isFetching reflects an actual in-flight
  // request, which is what the UI here needs.
  const { data: lanesData, isFetching: loadingLanes } = useTanStackQuery({
    queryKey: ['lane-map-lanes', cityId, zoom],
    queryFn: () => laneMapApi.fetchLanes({ cityId, zoom }),
    enabled: !!cityId,
    // `zoom` is part of the key (it drives server-side geometry
    // simplification), so any zoom change starts a fresh query. Without this,
    // the sidebar flashes "Pairs (0)" and every polyline disappears until the
    // refetch lands — very visible now that selecting a pair frames it, which
    // changes the zoom as a side effect. Keeping the previous page's data means
    // the list and the routes stay put and are simply replaced when the new
    // simplification arrives.
    keepPreviousData: true,
  });

  const { data: stationsData, isFetching: loadingStations } = useTanStackQuery({
    queryKey: ['lane-map-stations', bounds],
    queryFn: () => laneMapApi.fetchStations(bounds),
    enabled: !!bounds,
  });

  // Memoized (not a plain `|| []` fallback) so downstream hooks that depend on
  // this don't see a fresh array reference — and therefore a "changed"
  // dependency — on every render even though the underlying data hasn't moved.
  const routes = useMemo(() => lanesData?.lanes || [], [lanesData]);
  const stations = useMemo(() => stationsData?.stations || [], [stationsData]);

  // Every route the city has, grouped into unordered city pairs (see
  // `buildCityPairs` in ./laneGeometry, re-exported at the top of this file).
  // This is the sidebar's actual list — 30 routes collapse to 10 pairs for Austin.
  const pairs = useMemo(() => buildCityPairs(routes), [routes]);

  // A hidden pair's corridor overlays and per-route station/buffer fetches
  // must not draw or fetch — a pair the operator toggled invisible via its
  // colour swatch is supposed to be gone, not partially gone. Deriving this
  // rather than clearing `selectedPairKey` itself means unhiding the pair
  // restores exactly what was selected, instead of forcing the operator to
  // re-pick it from the list.
  const effectiveSelectedPairKey = hiddenPairKeys.includes(selectedPairKey) ? null : selectedPairKey;

  const selectedPair = useMemo(
    () => pairs.find((pair) => pair.key === effectiveSelectedPairKey) || null,
    [pairs, effectiveSelectedPairKey],
  );

  // Only operational routes draw a corridor band and get fetched for station
  // data (item 2) — a non-operational route still draws its (subdued)
  // polyline, but contributes no band and no station candidates.
  const operationalRoutes = useMemo(
    () => selectedPair?.operationalRoutes || [],
    [selectedPair],
  );

  // Frame the whole selected pair — every route's points, operational or not.
  // Without this, selecting a pair whose geometry sits outside the current
  // viewport looks like nothing happened. Tracks the pair it last framed so
  // this only fires when the SELECTION changes: re-running on every routes
  // change would fight the operator, since panning/zooming refetches `routes`.
  const framedPairKeyRef = useRef(null);
  useEffect(() => {
    if (!effectiveSelectedPairKey) {
      // Cleared or hidden — forget it, so re-selecting the same pair frames
      // again rather than being treated as already-framed.
      framedPairKeyRef.current = null;
      return;
    }
    if (!map || !selectedPair) return;
    if (framedPairKeyRef.current === effectiveSelectedPairKey) return;

    // Read through optionally: this effect keys off the map instance, not off
    // the API-loaded flag, so it can in principle run a tick before
    // `window.google` is populated. Framing is a convenience — never throw out
    // of render for it.
    const LatLngBounds = window.google?.maps?.LatLngBounds;
    if (!LatLngBounds) return;

    const latLngBounds = new LatLngBounds();
    let hasPoint = false;
    selectedPair.routes.forEach((route) => {
      geoJsonToPath(route.polylineGeoJson, route.id).forEach((point) => {
        latLngBounds.extend(point);
        hasPoint = true;
      });
    });
    if (!hasPoint) return;

    map.fitBounds(latLngBounds);
    framedPairKeyRef.current = effectiveSelectedPairKey;
  }, [map, effectiveSelectedPairKey, selectedPair]);

  // One fetchLaneStations call per operational route in the selected pair,
  // each still keyed on lane id only (never on radius or any limit) — station
  // filtering has to stay instant and off the network. `laneMapApi.fetchLaneStations`
  // returns each station's distances measured against THAT ONE route, so the
  // inversion below is what turns "N per-route station lists" into "one entry
  // per station carrying every route it showed up on".
  const laneStationsQueries = useQueries({
    queries: operationalRoutes.map((route) => ({
      queryKey: ['lane-stations', route.id],
      queryFn: () =>
        laneMapApi.fetchLaneStations({ laneId: route.id, maxRadiusMeters: 25000 }),
      enabled: !!selectedPair,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const pairStationsTruncated = laneStationsQueries.some((q) => q.data?.truncated);

  // The per-route station fetches land one at a time, and `pairStations` is
  // rebuilt on every one of them — so a count rendered before they've all
  // settled is a PARTIAL count that climbs as the rest arrive. On a 6-route
  // pair like Austin↔Houston that showed 89 while one fetch was still in
  // flight and 93 once it landed, which reads as a wrong number rather than an
  // incomplete one. Gate the count on all of them succeeding.
  // "Settled" means nothing is still in flight — an ERROR counts as settled.
  // Requiring every query to have *succeeded* meant a single failed fetch left
  // the row on "counting…" forever, which is how a transient failure on the
  // Fort Worth pair presented. A stuck spinner is a worse lie than a stated
  // failure.
  const pairStationsSettled =
    laneStationsQueries.length > 0 && laneStationsQueries.every((q) => !q.isLoading);

  // Any failure means the merged station set is incomplete, so the count would
  // be an undercount presented as fact. Say so instead of showing a number.
  const pairStationsFailed = laneStationsQueries.some((q) => q.status === 'error');

  // The inversion at the heart of this page (see laneEligibility.js's module
  // doc and the task's "data assembly" note): each per-route query result
  // carries stations measured against ONE route's polyline/city distances,
  // but with no notion of that route's origin/destination city names. Group
  // by station id across every route's results, and on each entry pair this
  // station's measurements against THIS route with THIS route's own city
  // names — never another route's. Getting this wrong (e.g. mixing up which
  // route's cities go with which route's distances) would produce a
  // confidently wrong tooltip.
  const pairStations = useMemo(() => {
    if (!selectedPair) return [];
    const byId = new window.Map();
    laneStationsQueries.forEach((query, index) => {
      const route = operationalRoutes[index];
      const stationRows = query.data?.stations || [];
      stationRows.forEach((stationRow) => {
        const existing =
          byId.get(stationRow.id) ||
          {
            id: stationRow.id,
            name: stationRow.name,
            isOperational: stationRow.isOperational,
            latitude: stationRow.latitude,
            longitude: stationRow.longitude,
            routes: [],
          };
        existing.routes.push({
          distanceToPolylineMeters: stationRow.distanceToPolylineMeters,
          distanceToOriginCityMeters: stationRow.distanceToOriginCityMeters,
          distanceToDestinationCityMeters: stationRow.distanceToDestinationCityMeters,
          originCityName: route.originCityName,
          destinationCityName: route.destinationCityName,
        });
        byId.set(stationRow.id, existing);
      });
    });
    return Array.from(byId.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPair, operationalRoutes, laneStationsQueries]);

  const pairStationsById = useMemo(
    () => new window.Map(pairStations.map((station) => [station.id, station])),
    [pairStations],
  );

  const { data: limitsPayload } = useTanStackQuery({
    queryKey: ['lane-map-limits'],
    queryFn: () => laneMapApi.fetchLimits(),
    staleTime: 5 * 60 * 1000,
  });

  const { values: liveLimits, sources: limitSources } = useMemo(
    () => flattenLiveLimits(limitsPayload),
    [limitsPayload],
  );

  const [limits, setLimits] = useState(null);

  // Seed from the live values once they land, applying any URL overrides so a
  // shared link reproduces the same view. Limits are view-local only: they
  // are never written to the backend, GrowthBook, or localStorage — just
  // React state and the URL query string.
  useEffect(() => {
    if (!liveLimits || limits) return;
    setLimits(deserializeLimits(new URLSearchParams(window.location.search), liveLimits));
  }, [liveLimits, limits]);

  const onLimitsChange = useCallback(
    (next) => {
      setLimits(next);
      history.replace({ search: buildURLParams(cityId, next, liveLimits).toString() });
    },
    [liveLimits, cityId, history],
  );

  const onResetLimits = useCallback(() => {
    if (liveLimits) onLimitsChange({ ...liveLimits });
  }, [liveLimits, onLimitsChange]);

  // How many stations are ELIGIBLE for the selected pair, for the sidebar row
  // and the map legend. countUsableStationsForPair takes one shared `routes`
  // list for every station it's given (see laneEligibility.js) — but each
  // station in `pairStations` carries its OWN per-route measurements, so it's
  // called once per station (with that station's own `.routes`) rather than
  // once for the whole list.
  // Null until every per-route fetch has settled, so the row never shows a
  // partial count as if it were the answer (see pairStationsSettled).
  const usableStationCount = useMemo(() => {
    if (!selectedPair || !limits || !pairStationsSettled || pairStationsFailed) return null;
    return pairStations.reduce(
      (count, station) => count + countUsableStationsForPair([station], station.routes, limits),
      0,
    );
  }, [selectedPair, limits, pairStations, pairStationsSettled, pairStationsFailed]);

  // Lane-scoped stations carry corridor distances (via `pairStationsById`);
  // viewport stations do not. Preferring the pair row here means an
  // overlapping station's marker is placed and labelled using the per-route
  // fetch's data, not the viewport fetch's. A station present only in the
  // viewport set has no eligibility state and renders neutrally.
  const stationsToRender = useMemo(() => {
    const merged = new window.Map();
    stations.forEach((s) => merged.set(s.id, s));
    pairStations.forEach((s) => merged.set(s.id, s));
    const all = Array.from(merged.values());
    // Filtered here, after the merge, so it applies to BOTH sources — the
    // viewport-wide set and the per-route sets. `usableStationCount` is
    // deliberately NOT filtered: a non-operational station can never be
    // eligible, so hiding them cannot change the count, and computing the
    // count off the visible subset would invite exactly that coupling.
    return visibleLayers.nonOperationalStations
      ? all
      : all.filter((station) => station.isOperational);
  }, [stations, pairStations, visibleLayers.nonOperationalStations]);

  // Debounced so dragging a slider issues one round of buffer requests when
  // the drag settles, not one per pixel. `staleTime: Infinity` on each
  // per-route query (see useRouteBuffers) plus a radius-keyed queryKey means
  // each (route, radius) pair is fetched once and served from cache
  // thereafter — dragging back to a previous value is free. Station
  // filtering itself never goes through this path; it stays synchronous on
  // the precomputed per-station distances.
  const debouncedCorridorRadius = useDebouncedValue(limits?.stationToPolylineMeters, 250);
  const debouncedSharedRadius = useDebouncedValue(limits?.sharedDoorRadiusMeters, 250);
  const debouncedAlongRouteRadius = useDebouncedValue(limits?.alongRouteBoundMeters, 250);

  // Corridor band: one buffer per operational route, overlaid translucently
  // (item 2). For Austin↔Houston (6 operational routes) with this layer on,
  // that's 6 fetchLaneBuffer calls.
  const stationCorridorBuffers = useRouteBuffers(
    operationalRoutes,
    debouncedCorridorRadius,
    visibleLayers.stationCorridor,
  );

  const sharedDoorBuffers = useRouteBuffers(
    operationalRoutes,
    debouncedSharedRadius,
    visibleLayers.sharedDoorBand,
  );

  // The general along-route bound — it gates whether private door is offered
  // at all (isWithinSharedDoorRadius in laneEligibility.js), not a fee tier.
  // Shares the `['lane-buffer', routeId, radius]` cache namespace with the two
  // bands above, so when this radius equals one of theirs the same fetch
  // serves both.
  const alongRouteBuffers = useRouteBuffers(
    operationalRoutes,
    debouncedAlongRouteRadius,
    visibleLayers.alongRouteBound,
  );

  // The lane-match outer bound reuses the same buffer endpoint at a fixed
  // (non-editable) radius, so no separate debounce is needed — this radius
  // never changes while the page is open.
  const laneMatchBuffers = useRouteBuffers(
    operationalRoutes,
    liveLimits?.laneMatchMeters,
    visibleLayers.laneMatchOuter,
  );

  const pairCityCoords = useMemo(
    () => (selectedPair ? cityCoordsForPair(selectedPair) : { coordsA: null, coordsB: null }),
    [selectedPair],
  );

  const onCityChange = useCallback(
    (city) => {
      // A city switch is a navigation (push, like the pre-existing
      // behaviour), but it must not silently drop whatever limit overrides
      // are currently in effect — the operator switching city is almost
      // certainly still experimenting with those thresholds, and the URL is
      // supposed to always reflect what's on screen so it stays shareable.
      // `limits` can still be `null` on very early renders before the live
      // values have loaded; buildURLParams skips serialising in that case.
      history.push({ search: buildURLParams(city?.id, limits, liveLimits).toString() });
      setHiddenPairKeys([]);
      // The previous city's pair selection is meaningless once `pairs` swaps
      // to the new city's set — `selectedPair` itself already resolves to
      // null (it looks the key up in the new `pairs` array), but
      // `effectiveSelectedPairKey` only gates on `hiddenPairKeys`, so without
      // this the old pair key would keep driving the buffer and station
      // queries for a pair that no longer appears anywhere in the UI.
      setSelectedPairKey(null);
      if (city && map && city.cityCoord?.coordinates) {
        const [lng, lat] = city.cityCoord.coordinates;
        map.panTo({ lat, lng });
        map.setZoom(8);
      }
    },
    [map, history, limits, liveLimits],
  );

  const togglePair = useCallback((pairKey) => {
    setHiddenPairKeys((prev) =>
      prev.includes(pairKey) ? prev.filter((key) => key !== pairKey) : [...prev, pairKey],
    );
  }, []);

  const visiblePairs = pairs.filter((pair) => !hiddenPairKeys.includes(pair.key));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={cardHeaderSx}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography sx={{ fontSize: '19px', fontWeight: 600, letterSpacing: '-0.01em', flex: 1 }}>
            Lane Map
          </Typography>
          <FilterSelect
            icon={<StationIcon />}
            label="City"
            placeholder="Pick a city"
            clearLabel="No city"
            options={citiesOptions}
            value={selectedCity}
            onChange={onCityChange}
            getOptionLabel={(c) => `${c.cityName}, ${c.stateAbbr}`}
          />
          <Button onClick={onSearchArea} disabled={!map} sx={pillButtonSx}>
            Search Area
          </Button>
          <Button
            onClick={() => setHiddenPairKeys([])}
            disabled={!hiddenPairKeys.length}
            sx={filterPillSx}
          >
            Show All Pairs
          </Button>
        </Stack>
      </Box>

      {!cityId && (
        <Alert severity="info" sx={{ mx: 4, mt: 1 }}>
          Pick a city to load the lanes running in and out of it. Stations load for whatever
          the map is looking at.
        </Alert>
      )}

      {lanesData?.truncated && (
        <Alert severity="warning" sx={{ mx: 4, mt: 1 }}>
          Showing the first {routes.length} routes — this city has more than the display cap.
        </Alert>
      )}

      {stationsData?.truncated && (
        <Alert severity="warning" sx={{ mx: 4, mt: 1 }}>
          Station results were capped for this viewport. Zoom in for a complete picture.
        </Alert>
      )}

      {pairStationsTruncated && (
        <Alert severity="warning" sx={{ mx: 4, mt: 1 }}>
          Station results were capped for one or more routes in this pair — the usable-station
          count and marker states below may be incomplete.
        </Alert>
      )}

      {/*
        Distinct from the truncation warning: there the data is complete but
        capped, here a request failed outright, so markers are missing rather
        than merely fewer. Without this an operator would read a half-drawn
        corridor as "this pair has few stations".
      */}
      {pairStationsFailed && (
        <Alert severity="error" sx={{ mx: 4, mt: 1 }}>
          Station data failed to load for one or more routes in this pair — markers and the station
          count are incomplete. Reselect the pair to retry.
        </Alert>
      )}

      {(loadingLanes || loadingStations) && (
        <Typography variant="body2" sx={{ px: 4, pt: 1 }} color="text.secondary">
          Loading...
        </Typography>
      )}

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Box sx={{ width: 340, overflowY: 'auto', px: 2, py: 1, borderRight: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Pairs ({pairs.length})
          </Typography>
          <Stack spacing={0.5}>
            {pairs.map((pair) => {
              const hidden = hiddenPairKeys.includes(pair.key);
              const selected = selectedPairKey === pair.key;
              const isEffectiveSelection = pair.key === effectiveSelectedPairKey;
              return (
                <React.Fragment key={pair.key}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    onClick={() => setSelectedPairKey(pair.key)}
                    sx={{
                      cursor: 'pointer',
                      opacity: hidden ? 0.4 : 1,
                      backgroundColor: selected ? 'action.selected' : undefined,
                      borderRadius: 1,
                    }}
                  >
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePair(pair.key);
                      }}
                      sx={{
                        width: 12,
                        height: 4,
                        borderRadius: 1,
                        cursor: 'pointer',
                        backgroundColor: laneColorFor(pair.key),
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {pair.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {pair.routes.length} route{pair.routes.length === 1 ? '' : 's'}
                    </Typography>
                    {/*
                      "N stations", not "N usable": sitting immediately after
                      "6 routes" in the same muted caption style, a bare "89
                      usable" reads as a second count of ROUTES. Naming the noun
                      is the whole fix. While the per-route fetches are still in
                      flight this renders "counting…" rather than a partial
                      number.
                    */}
                    {isEffectiveSelection && (
                      <Typography
                        variant="caption"
                        color={pairStationsFailed ? 'error.main' : 'text.secondary'}
                      >
                        {/*
                          One expression, not a chain of `cond && '...'`: a false
                          branch renders as the literal string "false" into the
                          caption, which is how this first came out as
                          "falsefalse1 station".
                        */}
                        {(() => {
                          if (pairStationsFailed) return 'count unavailable';
                          if (usableStationCount === null) return 'counting…';
                          return `${usableStationCount} station${usableStationCount === 1 ? '' : 's'}`;
                        })()}
                      </Typography>
                    )}
                  </Stack>
                  {/*
                    The pair row is a summary — "6 routes" — and until now
                    there was no way to reach one specific route. The polyline
                    editor works on a single lane, so the selected pair
                    expands into its lanes. Only the selected pair expands:
                    every pair expanded at once would bury the pair list,
                    which is how operators navigate.
                  */}
                  {selected && (
                    <Stack spacing={0.5} sx={{ pl: 3, py: 0.5 }}>
                      {pair.routes.map((route) => (
                        <Stack key={route.id} direction="row" spacing={1} alignItems="center">
                          <Typography variant="caption" sx={{ flex: 1 }}>
                            {route.originCityName} → {route.destinationCityName}
                          </Typography>
                          {canEditPolyline && (
                            <Button
                              size="small"
                              sx={pillButtonSx}
                              onClick={(e) => {
                                e.stopPropagation();
                                // Carry `cityId` through. The lanes query is
                                // `enabled: !!cityId`, so without it both the
                                // editor's Back link and its post-save
                                // redirect land on a lane map that fetches
                                // nothing — an empty page, exactly where the
                                // operator was sent to confirm the corridor
                                // actually moved. The editor already
                                // preserves this parameter; it just never
                                // received one.
                                history.push(
                                  `/location/lanes/${route.id}${
                                    cityId ? `?cityId=${encodeURIComponent(cityId)}` : ''
                                  }`,
                                );
                              }}
                            >
                              Edit polyline
                            </Button>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </React.Fragment>
              );
            })}
          </Stack>

          <Stack spacing={0.5} sx={{ mb: 2, mt: 2 }}>
            <Typography variant="subtitle2">Layers</Typography>
            {[
              ['stationCorridor', 'Station corridor'],
              ['sharedDoorBand', 'Shared door band'],
              ['alongRouteBound', 'Along-route bound'],
              ['farCityExclusion', 'Far-city exclusion'],
              ['laneMatchOuter', 'Lane-match outer bound'],
              ['cityRadius', 'City radius'],
            ].map(([key, label]) => (
              <Stack key={key} direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  size="small"
                  checked={visibleLayers[key]}
                  onChange={() => toggleLayer(key)}
                />
                <Typography variant="body2">{label}</Typography>
              </Stack>
            ))}
            {!selectedPairKey && (
              <Typography variant="caption" color="text.secondary">
                Select a pair to draw its corridors.
              </Typography>
            )}
          </Stack>

          {/*
            Its own group rather than a seventh "Layers" row: the others toggle
            a geometry overlay, this one filters the station markers.
          */}
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Stations</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Checkbox
                size="small"
                checked={visibleLayers.nonOperationalStations}
                onChange={() => toggleLayer('nonOperationalStations')}
              />
              <Typography variant="body2">Show non-operational</Typography>
            </Stack>
          </Stack>

          <LaneMapLimitsPanel
            limits={limits}
            sources={limitSources}
            liveLimits={liveLimits || {}}
            onChange={onLimitsChange}
            onResetAll={onResetLimits}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
          <Map
            center={DEFAULT_CENTER}
            zoom={DEFAULT_ZOOM}
            onLoad={onMapLoad}
            onIdle={onMapIdle}
            mapContainerStyle={{ width: '100%', height: '100%' }}
          >
            {visibleLayers.stationCorridor &&
              stationCorridorBuffers.map((query, index) => {
                const geojson = query.data?.geojson;
                if (!geojson) return null;
                return (
                  <Polygon
                    key={`corridor-${operationalRoutes[index].id}`}
                    paths={geoJsonToPolygonPaths(geojson)}
                    options={{
                      fillColor: '#4285F4',
                      fillOpacity: 0.1,
                      strokeColor: '#4285F4',
                      strokeOpacity: 0.6,
                      strokeWeight: 1,
                      clickable: false,
                    }}
                  />
                );
              })}

            {visibleLayers.sharedDoorBand &&
              sharedDoorBuffers.map((query, index) => {
                const geojson = query.data?.geojson;
                if (!geojson) return null;
                return (
                  <Polygon
                    key={`shared-${operationalRoutes[index].id}`}
                    paths={geoJsonToPolygonPaths(geojson)}
                    options={{
                      fillColor: '#34A853',
                      fillOpacity: 0.14,
                      strokeColor: '#34A853',
                      strokeOpacity: 0.7,
                      strokeWeight: 1,
                      clickable: false,
                    }}
                  />
                );
              })}

            {/*
              Deliberately fill-less, with a heavier stroke than the two bands
              above. At live values this geometry is exactly coincident with the
              station corridor (both 10,000 m), and a filled polygon would just
              muddy that band's colour and read as one shape. An unfilled outline
              sits legibly on top of whatever it coincides with, so the
              along-route bound stays identifiable even when it exactly overlaps.
            */}
            {visibleLayers.alongRouteBound &&
              alongRouteBuffers.map((query, index) => {
                const geojson = query.data?.geojson;
                if (!geojson) return null;
                return (
                  <Polygon
                    key={`along-route-${operationalRoutes[index].id}`}
                    paths={geoJsonToPolygonPaths(geojson)}
                    options={{
                      fillOpacity: 0,
                      strokeColor: '#F9AB00',
                      strokeOpacity: 0.95,
                      strokeWeight: 3,
                      clickable: false,
                    }}
                  />
                );
              })}

            {/*
              Non-directional: with pairs, a city can be either end of a route
              depending on direction, so — unlike the old single-lane view —
              there is no "the" pickup-exclusion city vs. "the" dropoff-exclusion
              city to draw with different emphasis. One same-styled circle per
              city in the pair.
            */}
            {visibleLayers.farCityExclusion && selectedPair && (
              <>
                {pairCityCoords.coordsA && (
                  <Circle
                    center={pairCityCoords.coordsA}
                    radius={limits?.farCityExclusionMeters || 0}
                    options={{
                      fillColor: '#EA4335',
                      fillOpacity: 0.06,
                      strokeColor: '#EA4335',
                      strokeOpacity: 0.5,
                      strokeWeight: 1,
                      clickable: false,
                    }}
                  />
                )}
                {pairCityCoords.coordsB && (
                  <Circle
                    center={pairCityCoords.coordsB}
                    radius={limits?.farCityExclusionMeters || 0}
                    options={{
                      fillColor: '#EA4335',
                      fillOpacity: 0.06,
                      strokeColor: '#EA4335',
                      strokeOpacity: 0.5,
                      strokeWeight: 1,
                      clickable: false,
                    }}
                  />
                )}
              </>
            )}

            {visibleLayers.cityRadius && selectedPair && (
              <>
                {pairCityCoords.coordsA && (
                  <Circle
                    center={pairCityCoords.coordsA}
                    radius={liveLimits?.cityRadiusMeters || 120000}
                    options={{
                      fillOpacity: 0,
                      strokeColor: '#9E9E9E',
                      strokeOpacity: 0.5,
                      strokeWeight: 1,
                      clickable: false,
                    }}
                  />
                )}
                {pairCityCoords.coordsB && (
                  <Circle
                    center={pairCityCoords.coordsB}
                    radius={liveLimits?.cityRadiusMeters || 120000}
                    options={{
                      fillOpacity: 0,
                      strokeColor: '#9E9E9E',
                      strokeOpacity: 0.5,
                      strokeWeight: 1,
                      clickable: false,
                    }}
                  />
                )}
              </>
            )}

            {visibleLayers.laneMatchOuter &&
              laneMatchBuffers.map((query, index) => {
                const geojson = query.data?.geojson;
                if (!geojson) return null;
                return (
                  <Polygon
                    key={`lane-match-${operationalRoutes[index].id}`}
                    paths={geoJsonToPolygonPaths(geojson)}
                    options={{
                      fillOpacity: 0,
                      strokeColor: '#616161',
                      strokeOpacity: 0.5,
                      strokeWeight: 1,
                      clickable: false,
                    }}
                  />
                );
              })}

            {/*
              One stroke per route, uniformly styled. Non-operational routes
              aren't filtered here — `buildCityPairs` already dropped them, so
              every route reaching this point is operational. No dashed
              encoding: direction is no longer a property of a row, so a
              solid/dashed split has nothing left to encode.
            */}
            {visiblePairs.map((pair) =>
              pair.routes.map((route) => (
                <Polyline
                  key={route.id}
                  path={geoJsonToPath(route.polylineGeoJson, route.id)}
                  options={{
                    strokeColor: laneColorFor(pair.key),
                    strokeOpacity: 0.9,
                    strokeWeight: 3,
                  }}
                />
              )),
            )}

            {stationsToRender.map((station) => {
              const pairStation = pairStationsById.get(station.id);
              let verdict = null;
              let availability = [];
              if (pairStation && limits) {
                verdict = getStationVerdictForPair(pairStation, pairStation.routes, limits);
                if (verdict === STATION_PAIR_VERDICTS.ELIGIBLE) {
                  availability = getStationAvailabilityForPair(
                    pairStation,
                    pairStation.routes,
                    limits,
                  );
                }
              }
              return (
                <Marker
                  key={station.id}
                  position={{ lat: station.latitude, lng: station.longitude }}
                  icon={stationIcon(station, verdict)}
                  title={stationTooltip(station, verdict, availability)}
                />
              );
            })}
          </Map>

          {/*
            Bottom-left, lifted clear of Google's own attribution. The other
            three corners are taken by Google's controls — mapTypeControl
            (top-left), fullscreenControl (top-right), streetView (bottom-right)
            — and overlapping any of them makes both unreadable.
          */}
          <Box sx={{ position: 'absolute', bottom: 30, left: 12, zIndex: 5 }}>
            <LaneMapLegend showNonOperational={visibleLayers.nonOperationalStations} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LaneMap;
